import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Typography,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { TODAY_SUBTABS } from './constants/tabs';
import { isQueueApprover } from './utils/access';
import { formatEmployeeThaiName } from '../../../utils/functions/employeeName';
import TodaySubTabBar from '../TodaySubTabBar';

import { useQueueTabs } from './hooks/useQueueTabs';
import { useRowSelection } from './hooks/useRowSelection';
import { useQueueAction } from './hooks/useQueueAction';
import { useApprovalModals } from './hooks/useApprovalModals';

import TabBar from './components/tables/TabBar';
import BulkActionBar from './components/tables/BulkActionBar';
import ExpenseTableHead from './components/tables/ExpenseTableHead';
import ExpenseRow from './components/tables/ExpenseRow';
import ExpenseCard from './components/tables/ExpenseCard';
import ExpenseDetailModal from './components/dialogs/ExpenseDetailModal';
import ProjectPopupDialog from './components/dialogs/ProjectPopupDialog';
import ActionConfirmDialog from './components/dialogs/ActionConfirmDialog';
import { getApproverSlots, getExpenseApproverSlots } from '../../../utils/functions/approverAccess';

// หน้ากลางของ /profile/payment-approval (PaymentApproval.js) และ /profile/expense-review
// (ExpenseReview.js) — 2 หน้านั้นแค่ส่ง mode (constants/modes.js) เข้ามา ไม่มี logic ของตัวเอง
// หน้านี้ทำหน้าที่แค่ประกอบ (compose) hook + component ย่อยแล้ว render เท่านั้น — logic ทั้งหมด
// (ดึงข้อมูล/เลือกแถว/action dialog/modal) อยู่ใน hooks/, ค่าคงที่/ฟังก์ชันล้วนอยู่ใน constants/ กับ
// utils/, ชิ้นส่วน UI อยู่ใน components/
export default function ExpenseQueuePage({ title, mode }) {
  const me = useSelector((state) => state.me);
  const info = useSelector((state) => state.info);
  const theme = useTheme();
  // < md = มือถือ/แท็บเล็ตแนวตั้ง — สลับจากตาราง (scroll กว้าง กดยาก) เป็น list การ์ดแนวตั้ง
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ชื่อผู้มีสิทธิ์ของ flag รอง (อีกหน้า — approver_settings key ใน mode.secondary.approverKey) —
  // ใช้โชว์ใน tooltip ของ chip flag รองตอนยัง PENDING ว่ากำลังรอใครอยู่ (pattern เดียวกับ
  // getApproverNames ใน components/Table/ExpensesTableApplove.js)
  const secondaryApproverNames = useMemo(() => {
    const cfg = (info?.approver_settings || []).find(
      (c) => c.key === mode.secondary.approverKey,
    );
    if (!cfg) return [];
    return getApproverSlots(cfg)
      .filter(Boolean)
      .map((a) => formatEmployeeThaiName(a))
      .filter(Boolean);
  }, [info?.approver_settings, mode.secondary.approverKey]);

  // ชื่อผู้มีสิทธิ์ "อนุมัติรายการเบิก" ทั่วไป (Expenses.status, ตั้งค่าที่ /finance/expense-approvers
  // — HrExpenseApproverSettings.js) เก็บเป็น field ตรงบน Information (expense_approver_1/2/3) คนละ
  // รูปแบบกับ approver_settings array ด้านบน (ของเก่ากว่า ใช้กับ payment/expense_reviewer เท่านั้น) —
  // ใช้โชว์ใน tooltip ของ chip สถานะรายการเบิกตอนยัง PENDING ว่ากำลังรอใครอยู่ในแต่ละขั้น
  // ครบ 10 ช่อง (expense_approver_1..10) — ONE_STEP โชว์ทุกคนที่ตั้งไว้, THREE_STEP ใช้แค่ index 0-2
  // (ดู getExpensePendingApproverNames ใน ExpenseRow.js)
  const expenseApproverNames = useMemo(
    () => getExpenseApproverSlots(info).map((a) => formatEmployeeThaiName(a) || null),
    [info],
  );
  const isOneStepExpenseApproval = (info?.expense_approval_mode || 'THREE_STEP') === 'ONE_STEP';

  const {
    tab,
    setTab,
    subTab,
    setSubTab,
    rows,
    isLoading,
    tabCounts,
    subTabCounts,
    fetchRows,
    fetchTabCounts,
    fetchSubTabCounts,
  } = useQueueTabs(mode);

  const {
    selectedIds,
    setSelectedIds,
    toggleSelectRow,
    allSelected,
    toggleSelectAll,
  } = useRowSelection(rows, tab === 'today' ? `today:${subTab}` : tab, mode.statusField);

  const {
    actionDialog,
    setActionDialog,
    note,
    setNote,
    busy,
    handleAction,
    handleBulkAction,
    submitAction,
  } = useQueueAction({
    mode,
    rows,
    selectedIds,
    onSaved: () => {
      setSelectedIds(new Set());
      fetchRows();
      fetchTabCounts();
      fetchSubTabCounts();
    },
  });

  const {
    detailModalId,
    detailModalOpen,
    openDetailModal,
    closeDetailModal,
    popupProjectId,
    setPopupProjectId,
  } = useApprovalModals();

  if (!info?._id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!isQueueApprover(me, info, mode.accessKey)) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <LockOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="h6" fontWeight={700}>
          คุณไม่มีสิทธิ์เข้าถึงหน้านี้
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {mode.noAccessText}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* แถบบน (หัวข้อ + แท็บ + แถบเลือกหลายรายการ) — ล็อกค้างบนสุดของ viewport ตอน scroll ทั้งหน้า */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 30,
          bgcolor: 'background.default',
        }}
      >
        <TabBar title={title} tab={tab} setTab={setTab} tabCounts={tabCounts} />
        {tab === 'today' && (
          <TodaySubTabBar
            subTabs={TODAY_SUBTABS}
            value={subTab}
            onChange={setSubTab}
            counts={subTabCounts}
          />
        )}
        <BulkActionBar
          mode={mode}
          selectedCount={selectedIds.size}
          onBulkAction={handleBulkAction}
        />
      </Box>

      {isMobile ? (
        // มุมมองมือถือ — list การ์ด (ไม่มีตาราง/scroll แนวนอน)
        <Box sx={{ px: 1, pb: 3 }}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={22} />
            </Box>
          )}
          {!isLoading && rows.length === 0 && (
            <Typography
              align="center"
              sx={{ py: 4, color: 'text.secondary', fontSize: '0.9rem' }}
            >
              {mode.emptyText}
            </Typography>
          )}
          {!isLoading && (
            <Stack spacing={1.5}>
              {rows.map((row, index) => (
                <ExpenseCard
                  key={row._id}
                  mode={mode}
                  row={row}
                  index={index}
                  selected={selectedIds.has(row._id)}
                  onToggleSelect={() => toggleSelectRow(row._id)}
                  onOpenDetail={() => openDetailModal(row._id)}
                  onAction={(action) => handleAction(row, action)}
                />
              ))}
            </Stack>
          )}
        </Box>
      ) : (
        // โครงเดียวกับ ExpensesTableApplove.js (/finance/expenses): TableContainer component={Paper} + Table size="small"
        // ความกว้างคอลัมน์ auto (ไม่ล็อก px) ไม่มี scroll ซ้อนในตาราง/ไม่มี sticky header
        <TableContainer component={Paper}>
          <Table size="small">
            <ExpenseTableHead
              mode={mode}
              allSelected={allSelected}
              selectedCount={selectedIds.size}
              onToggleSelectAll={toggleSelectAll}
            />
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    {mode.emptyText}
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                rows.map((row, index) => (
                  <ExpenseRow
                    key={row._id}
                    mode={mode}
                    row={row}
                    index={index}
                    selected={selectedIds.has(row._id)}
                    onToggleSelect={() => toggleSelectRow(row._id)}
                    onOpenProject={() => setPopupProjectId(row.project._id)}
                    onOpenDetail={() => openDetailModal(row._id)}
                    onAction={(action) => handleAction(row, action)}
                    secondaryApproverNames={secondaryApproverNames}
                    expenseApproverNames={expenseApproverNames}
                    isOneStepExpenseApproval={isOneStepExpenseApproval}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ExpenseDetailModal
        open={detailModalOpen}
        expenseId={detailModalId}
        onClose={closeDetailModal}
      />
      <ProjectPopupDialog
        projectId={popupProjectId}
        onClose={() => setPopupProjectId(null)}
      />
      <ActionConfirmDialog
        mode={mode}
        actionDialog={actionDialog}
        note={note}
        setNote={setNote}
        busy={busy}
        onCancel={() => setActionDialog(null)}
        onConfirm={submitAction}
      />
    </Box>
  );
}
